import { Component, OnInit, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ConfirmationService, MessageService } from 'primeng/api';
import { Observable, Subject, firstValueFrom, of } from 'rxjs';
import { catchError, shareReplay, startWith, switchMap, tap } from 'rxjs/operators';
import { ShiftSummaryClient, ShiftSummary } from '../../../services/nswag/nswagclient';

@Component({
  selector: 'app-shift-summary-list',
  standalone: false,
  templateUrl: './shift-summary-list.component.html',
  styleUrls: ['./shift-summary-list.component.css']
})
export class ShiftSummaryListComponent implements OnInit {
  jobId = '';
  summaries$!: Observable<ShiftSummary[]>;
  loading = signal(true);
  deletingSummaryId = signal<string | null>(null);
  private readonly refreshTrigger$ = new Subject<void>();

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private shiftSummaryClient: ShiftSummaryClient,
    private confirmationService: ConfirmationService,
    private messageService: MessageService
  ) {}

  ngOnInit(): void {
    this.summaries$ = this.route.paramMap.pipe(
      tap(params => {
        this.jobId = params.get('id') ?? '';
      }),
      switchMap(() => this.refreshTrigger$.pipe(
        startWith(undefined),
        tap(() => this.loading.set(true)),
        switchMap(() => this.shiftSummaryClient.getByJobId(this.jobId).pipe(
          catchError(() => of([] as ShiftSummary[]))
        ))
      )),
      tap(() => this.loading.set(false)),
      shareReplay(1)
    );
  }

  navigateToCreate(): void {
    this.router.navigate(['/vendorjob', this.jobId, 'shiftsummaries', 'new']);
  }

  navigateToView(summary: ShiftSummary): void {
    if (!summary.id) {
      return;
    }

    const path = summary.isSubmitted
      ? ['/vendorjob', this.jobId, 'shiftsummaries', summary.id, 'view']
      : ['/vendorjob', this.jobId, 'shiftsummaries', summary.id];

    this.router.navigate(path);
  }

  statusLabel(summary: ShiftSummary): string {
    return summary.isSubmitted ? 'Submitted' : 'Draft';
  }

  canDelete(summary: ShiftSummary): boolean {
    return !summary.isSubmitted;
  }

  confirmDelete(summary: ShiftSummary): void {
    if (!summary.id || !this.canDelete(summary)) {
      return;
    }

    this.confirmationService.confirm({
      key: 'confirmationDialog',
      header: 'Delete Confirmation',
      message: 'Are you sure you want to delete this shift summary?',
      icon: 'pi pi-exclamation-triangle',
      acceptButtonStyleClass: 'p-button-danger',
      accept: async () => this.deleteSummary(summary.id!)
    });
  }

  navigateBack(): void {
    this.router.navigate(['/vendorjob', this.jobId]);
  }

  private async deleteSummary(summaryId: string): Promise<void> {
    this.deletingSummaryId.set(summaryId);
    try {
      await firstValueFrom(this.shiftSummaryClient.delete(summaryId));
      this.messageService.clear('global-messages');
      this.messageService.add({ key: 'global-messages', severity: 'success', summary: 'Shift Summary Deleted', detail: '' });
      this.refreshTrigger$.next();
    } catch (error: any) {
      console.error('Error deleting shift summary', error);
      this.messageService.clear('global-messages');
      this.messageService.add({
        key: 'global-messages',
        severity: 'error',
        summary: 'Could not Delete Shift Summary',
        detail: error?.message ?? 'An unexpected error occurred.'
      });
    } finally {
      this.deletingSummaryId.set(null);
    }
  }
}
